import {
  File,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileArchive,
  Box,
  type LucideIcon,
} from 'lucide-react';
import { orderFileTypes } from '@insolech/sdk';

/**
 * Resolve a type-appropriate icon + short label for an order attachment, so the
 * file lists show at a glance whether a row is a PDF, spreadsheet, image, 3D
 * scan, etc. (before this, everything rendered a generic paperclip). Classifies
 * by extension first (reliable), falling back to the MIME type.
 */
export interface OrderFileIconInfo {
  Icon: LucideIcon;
  label: string;
}

export function orderFileIcon(fileName: string, mimeType?: string | null): OrderFileIconInfo {
  const ext = orderFileTypes.fileExtension(fileName);
  const m = (mimeType ?? '').toLowerCase();
  if (ext === 'pdf' || m === 'application/pdf') return { Icon: FileText, label: 'PDF' };
  if (['xlsx', 'xls', 'csv'].includes(ext) || m.includes('spreadsheet') || m.includes('excel')) {
    return { Icon: FileSpreadsheet, label: ext === 'csv' ? 'CSV' : 'Excel' };
  }
  if (m === 'text/csv') return { Icon: FileSpreadsheet, label: 'CSV' };
  if (['doc', 'docx'].includes(ext) || m.includes('word') || m === 'application/msword') {
    return { Icon: FileText, label: 'Word' };
  }
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) || m.startsWith('image/')) {
    return { Icon: FileImage, label: 'Image' };
  }
  if (['stl', 'obj', 'ply'].includes(ext) || m.startsWith('model/')) {
    return { Icon: Box, label: '3D model' };
  }
  if (ext === 'zip' || m.includes('zip')) return { Icon: FileArchive, label: 'Archive' };
  return { Icon: File, label: (ext || 'file').toUpperCase() };
}
